import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { FieldTooltip } from './FieldHeader.js';
import type { FieldValue, FieldValues } from './fieldValues.js';
import { Pill, PRIMARY_PILL_SX } from './Pill.js';
import type { FieldOptions } from './useFieldOptions.js';
import { WorkflowFieldControl } from './WorkflowFields.js';
import type { WorkflowStep } from './workflows.types.js';

const FIELDSET_RESET = { border: 0, m: 0, p: 0, minWidth: 0 };

interface WorkflowStepCardProps {
  step: WorkflowStep;
  stepNumber: number;
  values: FieldValues;
  onChange: (key: string, value: FieldValue) => void;
  optionsFor: FieldOptions;
  disabled: boolean;
  chained: boolean;
  /** Replaces the generic field controls where an entrypoint asks for its own. */
  controls?: ReactNode;
}

export function WorkflowStepCard({
  step,
  stepNumber,
  values,
  onChange,
  optionsFor,
  disabled,
  chained,
  controls,
}: WorkflowStepCardProps): ReactNode {
  return (
    <Stack data-testid={`workflow-step-${step.stepId}`} sx={{ gap: 5 }}>
      <Stack sx={{ gap: 2 }}>
        <Pill sx={[PRIMARY_PILL_SX, { alignSelf: 'flex-start' }]}>STEP {stepNumber}</Pill>
        <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center' }}>
          <Typography variant="h3" sx={{ fontSize: 22 }}>
            {step.title}
          </Typography>
          <FieldTooltip tooltip={step.tooltip} />
        </Stack>
        {step.description === null ? null : (
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{step.description}</Typography>
        )}
      </Stack>

      {controls !== undefined || step.fields.length > 0 ? (
        <Box
          component="fieldset"
          disabled={disabled}
          sx={[
            FIELDSET_RESET,
            step.layout === 'row'
              ? { display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'start' }
              : { display: 'flex', flexDirection: 'column', gap: 4 },
            disabled ? { opacity: 0.6 } : {},
          ]}
        >
          {controls ??
            step.fields.map((field) => (
              <WorkflowFieldControl
                key={field.key}
                field={field}
                values={values}
                optionsFor={optionsFor}
                onChange={onChange}
              />
            ))}
        </Box>
      ) : (
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          {chained
            ? 'Nothing to configure — this runs on what you picked above.'
            : 'Nothing to configure — this runs on the scope selected above.'}
        </Typography>
      )}
    </Stack>
  );
}
